import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from "lucide-react"
import { Slot } from "radix-ui"

const badgeVariants = cva(
  "inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-sm border border-transparent px-1.5 text-meta font-medium whitespace-nowrap focus-ring transition-[color,background-color,border-color] duration-fast ease-standard aria-invalid:border-destructive-border [&>svg]:pointer-events-none [&>svg]:size-3 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground [a&]:hover:bg-primary-hover",
        secondary:
          "bg-secondary text-secondary-foreground [a&]:hover:bg-secondary-hover",
        outline: "border-border text-foreground [a&]:hover:bg-secondary-hover",
        ghost:
          "text-muted-foreground [a&]:hover:bg-secondary-hover [a&]:hover:text-foreground",
        link: "text-primary underline-offset-4 [a&]:hover:underline",
        info: "border-info-border bg-info-bg text-info",
        success: "border-success-border bg-success-bg text-success",
        warning: "border-warning-border bg-warning-bg text-warning",
        destructive:
          "border-destructive-border bg-destructive-bg text-destructive",
        // Counts next to a label: tabs, sidebar items, filters.
        count:
          "min-w-5 rounded-full bg-muted px-1.5 text-muted-foreground tabular-nums",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

type StatusVariant = "info" | "success" | "warning" | "destructive"

// The one icon per status. Alert, AlertDialogMedia, toasts and field errors
// use it too, so a status always pairs its colour with the same shape.
const statusIcons: Record<StatusVariant, LucideIcon> = {
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  destructive: CircleAlertIcon,
}

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    /** Replaces the built-in status icon. Pass `null` to hide it. */
    icon?: React.ReactNode
  }

function Badge({
  className,
  variant = "default",
  icon,
  asChild = false,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot.Root : "span"
  const DefaultIcon =
    variant && variant in statusIcons
      ? statusIcons[variant as StatusVariant]
      : undefined
  const resolvedIcon =
    icon !== undefined ? icon : DefaultIcon ? <DefaultIcon aria-hidden /> : null

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    >
      {resolvedIcon}
      <Slot.Slottable>{children}</Slot.Slottable>
    </Comp>
  )
}

export { Badge, badgeVariants, statusIcons }
export type { BadgeProps, StatusVariant }
